/**
 * @file BedMap.tsx
 * @description Visual grid of all beds with color-coded status tiles and
 *              a hover tooltip showing patient details.
 *              Groups beds by ward for spatial clarity.
 */

import { useState } from "react";
import { UnifiedRecord, ResolvedBedStatus } from "../../types";

interface BedMapProps {
  records: UnifiedRecord[];
}

/** Status → tailwind classes for the tile */
const STATUS_CONFIG: Record<
  ResolvedBedStatus,
  { bg: string; border: string; label: string; dot: string }
> = {
  Occupied: {
    bg: "bg-red-500/20 hover:bg-red-500/35",
    border: "border-red-500/40",
    label: "Occupied",
    dot: "bg-red-400",
  },
  Available: {
    bg: "bg-green-500/20 hover:bg-green-500/35",
    border: "border-green-500/40",
    label: "Available",
    dot: "bg-green-400",
  },
  "Pending Clean": {
    bg: "bg-amber-500/20 hover:bg-amber-500/35",
    border: "border-amber-500/40",
    label: "Pending Clean",
    dot: "bg-amber-400",
  },
  "Bed Requires Cleaning/Check": {
    bg: "bg-orange-500/20 hover:bg-orange-500/35",
    border: "border-orange-500/40",
    label: "Requires Check",
    dot: "bg-orange-400",
  },
  Maintenance: {
    bg: "bg-gray-500/20 hover:bg-gray-500/35",
    border: "border-gray-500/40",
    label: "Maintenance",
    dot: "bg-gray-400",
  },
  Orphaned: {
    bg: "bg-purple-500/20 hover:bg-purple-500/35",
    border: "border-purple-500/40",
    label: "Orphaned",
    dot: "bg-purple-400",
  },
  Unknown: {
    bg: "bg-white/5 hover:bg-white/10",
    border: "border-white/10",
    label: "Unknown",
    dot: "bg-gray-600",
  },
};

interface BedTileProps {
  record: UnifiedRecord;
}

function BedTile({ record }: BedTileProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const config = STATUS_CONFIG[record.resolved_bed_status] ?? STATUS_CONFIG.Unknown;
  const isConflict = record.has_conflict;
  const bedLabel = record.resolved_bed || record.patient_id.replace("BED-ONLY-", "");

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        className={`relative rounded-xl border ${config.bg} ${config.border} p-3 cursor-pointer
                    transition-all duration-200 min-h-[72px] flex flex-col justify-between
                    ${isConflict ? "ring-1 ring-yellow-400/50" : ""}`}
      >
        {/* Conflict indicator */}
        {isConflict && (
          <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-yellow-400 border-2 border-navy-900 animate-pulse-slow" />
        )}

        {/* Bed ID */}
        <div className="text-xs font-mono font-bold text-white/80">{bedLabel}</div>

        {/* Status dot */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`w-2 h-2 rounded-full ${config.dot} flex-shrink-0`} />
          <span className="text-xs text-gray-400 truncate">
            {record.patient_name
              ? record.patient_name.split(" ")[0]
              : config.label}
          </span>
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 rounded-xl p-3 text-xs shadow-card animate-fade-in pointer-events-none"
          style={{
            background: "rgba(7, 20, 40, 0.98)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div className="font-semibold text-white mb-1.5">{bedLabel}</div>
          {record.patient_name && (
            <div className="text-gray-300 mb-1">
              👤 {record.patient_name}
            </div>
          )}
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`w-2 h-2 rounded-full ${config.dot}`} />
            <span className="text-gray-400">{config.label}</span>
          </div>
          {record.ward && (
            <div className="text-gray-500">Ward: {record.ward}</div>
          )}
          {record.his_clinical_status && (
            <div className="text-gray-500">HIS: {record.his_clinical_status}</div>
          )}
          {record.has_conflict && (
            <div className="mt-2 pt-2 border-t border-white/10">
              {record.conflict_flags
                .filter((f) => f !== "Clean")
                .map((f) => (
                  <div key={f} className="text-yellow-400 text-xs">
                    ⚠ {f}
                  </div>
                ))}
            </div>
          )}
          {/* Tooltip arrow */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-full"
            style={{
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid rgba(255,255,255,0.12)",
            }}
          />
        </div>
      )}
    </div>
  );
}

export function BedMap({ records }: BedMapProps) {
  // Exclude "orphaned lab" records from the bed map (they have no physical bed)
  const bedRecords = records.filter((r) => !r.is_orphaned_lab_record);

  // Group by ward
  const byWard = bedRecords.reduce<Record<string, UnifiedRecord[]>>((acc, r) => {
    const ward = r.ward || "Unassigned";
    if (!acc[ward]) acc[ward] = [];
    acc[ward].push(r);
    return acc;
  }, {});

  // Legend
  const legendItems: Array<{ status: ResolvedBedStatus }> = [
    { status: "Occupied" },
    { status: "Available" },
    { status: "Pending Clean" },
    { status: "Bed Requires Cleaning/Check" },
    { status: "Maintenance" },
  ];

  return (
    <div
      className="rounded-2xl border border-white/8 p-6"
      style={{
        background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-white font-semibold text-base">Bed Map</h2>
        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {legendItems.map(({ status }) => {
            const cfg = STATUS_CONFIG[status];
            return (
              <div key={status} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-sm ${cfg.dot.replace("bg-", "bg-")}`} />
                <span className="text-gray-500 text-xs">{cfg.label}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-yellow-400/60 ring-1 ring-yellow-400" />
            <span className="text-gray-500 text-xs">⚠ Conflict</span>
          </div>
        </div>
      </div>

      {/* Ward sections */}
      <div className="space-y-6">
        {Object.entries(byWard).sort(([a], [b]) => a.localeCompare(b)).map(([ward, wardRecords]) => (
          <div key={ward}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {ward}
              </h3>
              <div className="flex-1 h-px bg-white/5" />
              <span className="text-xs text-gray-600">{wardRecords.length} beds</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {wardRecords.map((r) => (
                <BedTile key={r.patient_id} record={r} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
